import React, { useState, useEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import Sidebar from '../../components/SideBar';
import Header from '../../components/Header';
import useGlobale from '../../components/global';
import DemandeurMenu from '../../components/DemandeurMenu';
import api from '../../components/api';
import { toast } from 'react-toastify';

import { colors, globalStyles } from './constants';
import { getLatestExperience } from './utils';
import SearchFilters from './SearchFilters';
import ActiveFilters from './ActiveFilters';
import DemandeurCard from './DemandeurCard';
import DemandeurModal from './DemandeurModal';
import Pagination from './Pagination';
import ExportMenu from './ExportMenu';

const ListeDemandeur = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user } = useGlobale();
  const [selectedDemandeur, setSelectedDemandeur] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [demandeurs, setDemandeurs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 12;

  // États pour les filtres avancés
  const [ageRange, setAgeRange] = useState({ min: 18, max: 65 });
  const [experienceYearsRange, setExperienceYearsRange] = useState({ min: 0, max: 20 });
  const [experienceFilter, setExperienceFilter] = useState('');
  const [competenceFilter, setCompetenceFilter] = useState('');
  const [diplomeFilter, setDiplomeFilter] = useState('');
  const [langueFilter, setLangueFilter] = useState('');

  // Données dérivées pour les filtres
  const allExperienceDomains = useMemo(() => {
    return [...new Set(
      demandeurs.flatMap(demandeur => 
        demandeur.experiences.map(exp => exp.poste)
      )
    )].sort();
  }, [demandeurs]);

  const allCompetences = useMemo(() => {
    return [...new Set(
      demandeurs.flatMap(demandeur => 
        demandeur.competences.map(comp => comp.description)
      )
    )].sort();
  }, [demandeurs]);
  
  const allDiplomes = useMemo(() => {
    return [...new Set(
      demandeurs.flatMap(demandeur => 
        demandeur.diplomes.map(dip => dip.type_diplome_nom)
      )
    )].sort();
  }, [demandeurs]);
  
  const allLangues = useMemo(() => {
    return [...new Set(
      demandeurs.flatMap(demandeur => 
        demandeur.langues.map(lang => lang.langue_nom)
      )
    )].sort();
  }, [demandeurs]);

  // Sidebar responsive
  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch demandeurs avec recherche côté serveur
  useEffect(() => {
    fetchDemandeurs(currentPage, searchTerm);
  }, [currentPage]);

  // Recherche avec debounce (attendre 500ms après la dernière frappe)
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchDemandeurs(1, searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchDemandeurs = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
      if (search.trim()) params.append('search', search.trim());
      const response = await api.get(`demandeur/demandeurs/?${params.toString()}`);
      setDemandeurs(response.data.results || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalCount(response.data.count || 0);
    } catch (error) {
      console.error('Erreur lors de la récupération des demandeurs:', error);
      toast.error('Erreur lors de la récupération des demandeurs');
    } finally {
      setLoading(false);
    }
  };

  const openDemandeurDetails = (demandeur) => {
    setSelectedDemandeur(demandeur);
    setShowModal(true);
  };

  const handleExportCV = async (demandeur) => {
    try {
      toast.info('Génération du CV en cours...', { autoClose: 2000 });
      
      // Call API to generate and download CV
      const response = await api.get(`demandeur/${demandeur.id}/export-cv/`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CV_${demandeur.user.first_name}_${demandeur.user.last_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CV exporté avec succès !');
    } catch (error) {
      console.error('Erreur lors de l\'export du CV:', error);
      toast.error('Erreur lors de l\'export du CV');
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F4F8E9]">
      <style>{globalStyles}</style>
      <Sidebar
        isOpen={isSidebarOpen}
        menuItems={DemandeurMenu()}
        onClose={() => setIsSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col w-full">
        <Header
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          user={user}
        />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-20 lg:pb-8 w-full">
          {/* En-tête de la page */}
          <div className="mb-8 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-[#437011]">
                Liste des demandeurs d'emploi
              </h1>
              <p className="text-gray-600">
                Consultez et gérez les profils des demandeurs d'emploi enregistrés
              </p>
            </div>
            
            <ExportMenu
              showExportOptions={showExportOptions}
              setShowExportOptions={setShowExportOptions}
              searchTerm={searchTerm}
            />
          </div>

          {/* Recherche et filtres */}
          <SearchFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterActive={filterActive}
            setFilterActive={setFilterActive}
            ageRange={ageRange}
            setAgeRange={setAgeRange}
            experienceYearsRange={experienceYearsRange}
            setExperienceYearsRange={setExperienceYearsRange}
            experienceFilter={experienceFilter}
            setExperienceFilter={setExperienceFilter}
            competenceFilter={competenceFilter}
            setCompetenceFilter={setCompetenceFilter}
            diplomeFilter={diplomeFilter}
            setDiplomeFilter={setDiplomeFilter}
            langueFilter={langueFilter}
            setLangueFilter={setLangueFilter}
            allExperienceDomains={allExperienceDomains}
            allCompetences={allCompetences}
            allDiplomes={allDiplomes}
            allLangues={allLangues}
          />
          
          {/* Filtres actifs */}
          <ActiveFilters
            filterActive={filterActive}
            ageRange={ageRange}
            setAgeRange={setAgeRange}
            experienceYearsRange={experienceYearsRange}
            setExperienceYearsRange={setExperienceYearsRange}
            experienceFilter={experienceFilter}
            setExperienceFilter={setExperienceFilter}
            competenceFilter={competenceFilter}
            setCompetenceFilter={setCompetenceFilter}
            diplomeFilter={diplomeFilter}
            setDiplomeFilter={setDiplomeFilter}
            langueFilter={langueFilter}
            setLangueFilter={setLangueFilter}
            totalCount={totalCount}
            currentPage={currentPage}
            totalPages={totalPages}
          />

          {/* Liste des demandeurs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {demandeurs.map((demandeur) => (
              <DemandeurCard
                key={demandeur.id}
                demandeur={demandeur}
                onOpenDetails={openDemandeurDetails}
                onExportCV={handleExportCV}
              />
            ))}
          </div>
          
          {/* Message si aucun résultat */}
          {demandeurs.length === 0 && !loading && (
            <div className="bg-white rounded-xl shadow-md p-8 text-center">
              <div className="text-gray-500 mb-2">
                <Search className="h-12 w-12 mx-auto opacity-50" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun résultat trouvé</h3>
              <p className="text-gray-500">
                Aucun demandeur ne correspond à votre recherche "{searchTerm}".
              </p>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: colors.primary }}></div>
            </div>
          )}

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
          />
        </main>
      </div>

      {/* Modal de détails */}
      {showModal && selectedDemandeur && (
        <DemandeurModal
          demandeur={selectedDemandeur}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default ListeDemandeur;
